import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { extractPluginError } from '@heidi/plugin-core';
import {
  type BluetoothStatus,
  type ChronicleBatteryInfo,
  type ChronicleDeviceSummary,
  type ChronicleStorageInfo,
  type ConnectResult,
  type DeviceState,
  type FirmwareFileInfo,
  type NetworkStatus,
} from './types';

const COMMAND_PREFIX = 'plugin:chronicle|';

class ChronicleBridgeError extends Error {
  readonly errorCode?: string;
  constructor(message: string, errorCode?: string) {
    super(message);
    this.name = 'ChronicleBridgeError';
    this.errorCode = errorCode;
  }
}

async function invokeChronicleCommand<T>(
  command: string,
  payload?: Record<string, unknown>
): Promise<T> {
  if (!isTauri()) {
    throw new ChronicleBridgeError(
      'Chronicle bridge is only available inside the Tauri runtime'
    );
  }

  const { invoke } = await import('@tauri-apps/api/core');
  try {
    return await invoke<T>(`${COMMAND_PREFIX}${command}`, payload ?? {});
  } catch (error) {
    const { message, errorCode } = extractPluginError(error);

    logger.debug('Chronicle command error', { command, message, errorCode });

    throw new ChronicleBridgeError(message, errorCode);
  }
}

export const chronicleBridge = {
  isAvailable(): boolean {
    return isTauri();
  },

  /**
   * Check Bluetooth availability and permission status.
   * Call this before starting a scan to provide appropriate error messages.
   *
   * @returns BluetoothStatus with availability and permission info
   */
  async checkBluetooth(): Promise<BluetoothStatus> {
    return invokeChronicleCommand<BluetoothStatus>('check_bluetooth');
  },

  /**
   * Open the system Bluetooth settings panel (for turning Bluetooth on/off).
   * Use this when Bluetooth is disabled.
   */
  async openBluetoothSettings(): Promise<void> {
    return invokeChronicleCommand<void>('open_bluetooth_settings');
  },

  /**
   * Open the system Privacy & Security → Bluetooth settings panel.
   * Use this when the user needs to grant Bluetooth permission to the app.
   */
  async openBluetoothPrivacySettings(): Promise<void> {
    return invokeChronicleCommand<void>('open_bluetooth_privacy_settings');
  },

  /**
   * Check network connectivity by attempting a TCP connection to reliable hosts.
   * Use this in the prerequisite checklist before device pairing.
   */
  async checkNetwork(): Promise<NetworkStatus> {
    return invokeChronicleCommand<NetworkStatus>('check_network');
  },

  /**
   * Open the system network settings panel.
   * macOS: System Settings → Network, Windows: Settings → Network & Wi-Fi.
   */
  async openNetworkSettings(): Promise<void> {
    return invokeChronicleCommand<void>('open_network_settings');
  },

  /**
   * Forget (unpair) the currently paired device.
   * Sends APP&BLE&RESET when connected and clears the stored identity.
   * `requireReset` fails the call instead of skipping the reset when no link is up.
   */
  async forgetDevice(options?: { requireReset?: boolean }): Promise<void> {
    return invokeChronicleCommand<void>('forget_device', {
      args: { requireReset: options?.requireReset ?? false },
    });
  },

  /**
   * Clear saved reconnect peripheral + name without factory-reset or wiping
   * the account binding code. Used when onboarding link is refused (403).
   * If either saved id or name matches the refused device, both are cleared;
   * otherwise both are kept.
   */
  async clearReconnectTarget(options: {
    deviceId: string;
    deviceName?: string;
  }): Promise<void> {
    return invokeChronicleCommand<void>('clear_reconnect_target', {
      args: {
        deviceId: options.deviceId,
        deviceName: options.deviceName,
      },
    });
  },

  /**
   * APP-4861: User-initiated reconnect.
   *
   * Always runs a fresh connection attempt. If the Rust `DeviceMonitor` is
   * between reconnect attempts (or `scan_start` is mid-flight), the in-flight
   * cycle is aborted before the new attempt starts. Used by the manual
   * "Reconnect" button.
   *
   * @param options.bindingCode - Server-fetched binding code (preferred over Keychain)
   * @param options.deviceName - BLE advertised name for Tier 2 name matching
   */
  async forceReconnect(options?: {
    bindingCode?: string;
    deviceName?: string;
  }): Promise<ConnectResult> {
    const args =
      options?.bindingCode !== undefined || options?.deviceName !== undefined
        ? { bindingCode: options?.bindingCode, deviceName: options?.deviceName }
        : undefined;
    return invokeChronicleCommand<ConnectResult>('force_reconnect', {
      args,
    });
  },

  /**
   * APP-4861: Startup / system-driven connect.
   *
   * Early-bails (no-op) when the actor is already connected, connecting,
   * scanning, or reconnecting. Used by `useChronicleStartupAutoConnect` on
   * app launch — never interrupts an in-flight monitor reconnect.
   *
   * @param options.bindingCode - Server-fetched binding code (preferred over Keychain)
   * @param options.deviceName - BLE advertised name for Tier 2 name matching
   */
  async ensureConnectedIdle(options?: {
    bindingCode?: string;
    deviceName?: string;
  }): Promise<ConnectResult> {
    const args =
      options?.bindingCode !== undefined || options?.deviceName !== undefined
        ? { bindingCode: options?.bindingCode, deviceName: options?.deviceName }
        : undefined;
    return invokeChronicleCommand<ConnectResult>('ensure_connected_idle', {
      args,
    });
  },

  async startScan(timeoutSec?: number): Promise<ChronicleDeviceSummary[]> {
    return invokeChronicleCommand<ChronicleDeviceSummary[]>('start_scan', {
      args: { timeoutSec },
    });
  },

  async stopScan(): Promise<void> {
    return invokeChronicleCommand<void>('stop_scan');
  },

  /**
   * APP-7553: persist the server-fetched binding code to the keychain ahead of
   * any sync. USB-only / auto-reconciler machines never run the BLE connect
   * flow (the only other path that writes the keychain), so without this they
   * decrypt bulk-sync chunks with the all-zero default key and upload garbage.
   */
  async setBindingCode(bindingCode: string): Promise<void> {
    return invokeChronicleCommand<void>('set_binding_code', {
      args: { bindingCode },
    });
  },

  async connect(
    deviceId: string,
    bindingCode?: string,
    deviceName?: string
  ): Promise<ConnectResult> {
    return invokeChronicleCommand<ConnectResult>('connect', {
      args: { deviceId, bindingCode, deviceName },
    });
  },

  async disconnect(): Promise<void> {
    return invokeChronicleCommand<void>('disconnect');
  },

  async startRecording(args?: { sessionId?: string }): Promise<void> {
    if (args?.sessionId) {
      return invokeChronicleCommand<void>('start_recording', {
        args: { sessionId: args.sessionId },
      });
    }
    return invokeChronicleCommand<void>('start_recording');
  },

  async pauseRecording(): Promise<void> {
    return invokeChronicleCommand<void>('pause_recording');
  },

  async resumeRecording(): Promise<void> {
    return invokeChronicleCommand<void>('resume_recording');
  },

  async stopRecording(): Promise<string> {
    return invokeChronicleCommand<string>('stop_recording');
  },

  async getState(): Promise<DeviceState> {
    return invokeChronicleCommand<DeviceState>('get_state');
  },

  async getPendingRecordings(): Promise<{ dir: string; name: string }[]> {
    return invokeChronicleCommand<{ dir: string; name: string }[]>(
      'get_pending_recordings'
    );
  },

  async syncFiles(files: { dir: string; name: string }[]): Promise<void> {
    return invokeChronicleCommand<void>('sync_files', { args: { files } });
  },

  async getBatteryInfo(): Promise<ChronicleBatteryInfo> {
    return invokeChronicleCommand<ChronicleBatteryInfo>('get_battery_info');
  },

  async getStorageInfo(): Promise<ChronicleStorageInfo> {
    return invokeChronicleCommand<ChronicleStorageInfo>('get_storage_info');
  },

  async deleteRecordings(files: { dir: string; name: string }[]): Promise<void> {
    return invokeChronicleCommand<void>('delete_recordings', {
      args: { files },
    });
  },

  async setUsbMode(enabled: boolean): Promise<boolean | null> {
    return invokeChronicleCommand<boolean | null>('set_usb_mode', {
      args: { enabled },
    });
  },

  async getUsbMode(): Promise<boolean | null> {
    return invokeChronicleCommand<boolean | null>('get_usb_mode');
  },

  /** Set auto power-off timer (`APP&SHUTDOWN&{minutes}`). `0` disables. */
  async setAutoPowerOff(minutes: number): Promise<number | null> {
    return invokeChronicleCommand<number | null>('set_auto_power_off', {
      args: { minutes },
    });
  },

  async getAutoPowerOff(): Promise<number | null> {
    return invokeChronicleCommand<number | null>('get_auto_power_off');
  },

  /**
   * Start firmware update via BLE OTA.
   * Progress is reported through chronicle:ota_state events.
   *
   * Supports two modes:
   * - Remote: pass `downloadUrl` + `targetVersion` — Rust downloads, caches, then OTA
   * - Local: pass `filePath` — Rust reads local file then OTA (debug option)
   */
  async startFirmwareUpdate(
    args: {
      mode?: 'ble';
    } & (
      | {
          downloadUrl: string;
          targetVersion: string;
          deviceModel?: string;
          filePath?: never;
        }
      | {
          filePath: string;
          downloadUrl?: never;
          targetVersion?: never;
          deviceModel?: never;
        }
    )
  ): Promise<void> {
    return invokeChronicleCommand<void>('start_firmware_update', {
      args: {
        mode: args.mode ?? 'ble',
        filePath: args.filePath,
        downloadUrl: args.downloadUrl,
        targetVersion: args.targetVersion,
        deviceModel: args.deviceModel,
      },
    });
  },

  /**
   * Cancel an in-progress firmware update.
   */
  async cancelFirmwareUpdate(): Promise<void> {
    return invokeChronicleCommand<void>('cancel_firmware_update');
  },

  /**
   * Open a native file dialog to select a firmware `.bin` file.
   * Returns file info if selected, or `null` if the user cancelled.
   * Follows the same pattern as iOS: filter to `.bin` only, no content validation.
   */
  async selectFirmwareFile(title?: string): Promise<FirmwareFileInfo | null> {
    if (!isTauri()) {
      throw new ChronicleBridgeError(
        'Chronicle bridge is only available inside the Tauri runtime'
      );
    }

    const { open } = await import('@tauri-apps/plugin-dialog');
    const filePath = await open({
      multiple: false,
      filters: [{ name: 'Firmware', extensions: ['bin'] }],
      title: title ?? 'Select Firmware File',
    });

    if (!filePath) return null;

    const segments = filePath.split(/[/\\]/);
    const fileName = segments[segments.length - 1] ?? filePath;

    return {
      path: filePath,
      fileName,
    };
  },
};

export { ChronicleBridgeError };
